'use client';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import dynamic from 'next/dynamic';
import ComponentPalette from './ComponentPalette';
import CanvasToolbar from './CanvasToolbar';
import NodeMonitorPopup from './NodeMonitorPopup';
import NodeInspectorPanel from './NodeInspectorPanel';
import BottleneckPanel from './BottleneckPanel';
import PathMonitorDrawer from './PathMonitorDrawer';
import PresetsPanel from './PresetsPanel';
import SaveLoadModal from './SaveLoadModal';
import ComparePanel from './ComparePanel';
import LiveMetricsBar from './LiveMetricsBar';
import type { CanvasNode, CanvasEdge, ArchitecturePreset } from '@/lib/archsim-data';
import { ALL_COMPONENTS } from '@/lib/archsim-data';
import { computeSystemMetrics, propagateTraffic, createInitialChaosState, toggleChaosEvent, expireChaosEvents, type ChaosState, type ChaosEventId,  } from '@/lib/simulation-engine';
import { recordSimulationSession } from '@/lib/progressTracker';

// Dynamic import for the canvas SVG layer to avoid SSR
const CanvasLayer = dynamic(() => import('./CanvasLayer'), { ssr: false });

export type SimulationState = 'stopped' | 'running' | 'paused';

export interface SimMetrics {
  rps: number;
  p99: number;
  errorRate: number;
  availability: number;
  cacheHit: number;
  dbLoad: number;
  queueDepth: number;
  tick: number;
}

const INITIAL_METRICS: SimMetrics = {
  rps: 0, p99: 0, errorRate: 0, availability: 100, cacheHit: 0, dbLoad: 0, queueDepth: 0, tick: 0,
};

export default function ArchitectureCanvasScreen() {
  const [nodes, setNodes] = useState<CanvasNode[]>([]);
  const [edges, setEdges] = useState<CanvasEdge[]>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [simState, setSimState] = useState<SimulationState>('stopped');
  const [simMetrics, setSimMetrics] = useState<SimMetrics>(INITIAL_METRICS);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [bottleneckOpen, setBottleneckOpen] = useState(false);
  const [pathMonitorOpen, setPathMonitorOpen] = useState(false);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [saveLoadOpen, setSaveLoadOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [connectingFrom, setConnectingFrom] = useState<string | null>(null);
  const [chaosState, setChaosState] = useState<ChaosState>(createInitialChaosState());
  const [monitorPopup, setMonitorPopup] = useState<{ nodeId: string; x: number; y: number } | null>(null);
  const [isLandscape, setIsLandscape] = useState(false);

  // Detect landscape mobile
  useEffect(() => {
    const check = () => {
      setIsLandscape(window.innerHeight < 500 && window.innerWidth > window.innerHeight);
    };
    check();
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);

  const simIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tickRef = useRef(0);
  const nodesRef = useRef<CanvasNode[]>(nodes);
  const edgesRef = useRef<CanvasEdge[]>(edges);
  const chaosRef = useRef<ChaosState>(chaosState);
  const cumulativeErrorRef = useRef<number>(0);
  const errorTickCountRef = useRef<number>(0);

  // Keep refs in sync
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  useEffect(() => { edgesRef.current = edges; }, [edges]);
  useEffect(() => { chaosRef.current = chaosState; }, [chaosState]);

  const selectedNode = nodes.find(n => n.id === selectedNodeId) ?? null;

  // ── Simulation tick — physically accurate ──────────────────────────────────
  const runSimulationTick = useCallback(() => {
    tickRef.current += 1;
    const tick = tickRef.current;
    const currentNodes = nodesRef.current;
    const currentEdges = edgesRef.current;

    // Auto-expire chaos events
    const freshChaos = expireChaosEvents(chaosRef.current, tick);
    if (freshChaos !== chaosRef.current) {
      chaosRef.current = freshChaos;
      setChaosState(freshChaos);
    }
    const chaos = chaosRef.current;

    // ── Two-pass traffic propagation ─────────────────────────────────────────
    const connectedClientRps = computeConnectedClientRps(currentNodes, currentEdges);

    const { updatedNodes } = propagateTraffic(currentNodes, currentEdges, chaos, tick);

    setNodes(updatedNodes);

    // ── System-level metrics ──────────────────────────────────────────────────
    const sys = computeSystemMetrics(updatedNodes, currentEdges, connectedClientRps, tick, chaos);

    setSimMetrics({
      rps: sys.rps,
      p99: sys.p99,
      errorRate: sys.errorRate,
      availability: sys.availability,
      cacheHit: sys.cacheHit,
      dbLoad: sys.dbLoad,
      queueDepth: sys.queueDepth,
      tick,
    });

    // Accumulate error rate for session average
    cumulativeErrorRef.current += sys.errorRate;
    errorTickCountRef.current += 1;
  }, []);

  useEffect(() => {
    if (simState === 'running') {
      setConnectingFrom(null);
      cumulativeErrorRef.current = 0;
      errorTickCountRef.current = 0;
      simIntervalRef.current = setInterval(runSimulationTick, 800);
    } else {
      if (simIntervalRef.current) clearInterval(simIntervalRef.current);
      if (simState === 'stopped') {
        // Record session progress before resetting
        const ticks = tickRef.current;
        if (ticks > 0 && nodesRef.current.length > 0) {
          const defIds = nodesRef.current.map(n => n.defId);
          const avgError = errorTickCountRef.current > 0
            ? cumulativeErrorRef.current / errorTickCountRef.current / 100
            : 0;
          recordSimulationSession(defIds, ticks, avgError);
        }
        tickRef.current = 0;
        cumulativeErrorRef.current = 0;
        errorTickCountRef.current = 0;
        setChaosState(createInitialChaosState());
        setSimMetrics(INITIAL_METRICS);
        setNodes(prev => prev.map(n => ({ ...n, status: 'idle', metrics: { rps: 0, latency: 0, errorRate: 0, cpuLoad: 0 } })));
      }
    }
    return () => { if (simIntervalRef.current) clearInterval(simIntervalRef.current); };
  }, [simState, runSimulationTick]);

  // ── Chaos event handler (exposed to PathMonitorDrawer) ────────────────────
  const handleToggleChaos = useCallback((eventId: ChaosEventId) => {
    setChaosState(prev => {
      const next = toggleChaosEvent(prev, eventId, nodesRef.current, tickRef.current);
      chaosRef.current = next;
      return next;
    });
  }, []);

  const handleDropComponent = useCallback((defId: string, x: number, y: number) => {
    const def = ALL_COMPONENTS.find(c => c.id === defId);
    if (!def) return;
    const id = `node-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const newNode: CanvasNode = {
      id,
      defId,
      x,
      y,
      label: def.name,
      status: 'idle',
      metrics: { rps: 0, latency: 0, errorRate: 0, cpuLoad: 0 },
      config: {},
    };
    setNodes(prev => [...prev, newNode]);
  }, []);

  const handleMoveNode = useCallback((id: string, x: number, y: number) => {
    setNodes(prev => prev.map(n => n.id === id ? { ...n, x, y } : n));
  }, []);

  const handleDeleteNode = useCallback((id: string) => {
    setNodes(prev => prev.filter(n => n.id !== id));
    setEdges(prev => prev.filter(e => e.sourceId !== id && e.targetId !== id));
    if (selectedNodeId === id) { setSelectedNodeId(null); setInspectorOpen(false); }
  }, [selectedNodeId]);

  const handleSelectNode = useCallback((id: string | null) => {
    setSelectedNodeId(id);
  }, []);

  const handleSelectEdge = useCallback((id: string | null) => {
    setSelectedEdgeId(id);
    if (id) setPathMonitorOpen(true);
  }, []);

  const handleNodeClickForPopup = useCallback((id: string, screenX: number, screenY: number) => {
    // If connecting, don't show popup
    if (connectingFrom) return;
    setMonitorPopup(prev => prev?.nodeId === id ? null : { nodeId: id, x: screenX, y: screenY });
  }, [connectingFrom]);

  const handleConnectNodes = useCallback((sourceId: string, targetId: string) => {
    if (sourceId === targetId) return;
    const exists = edges.some(e => e.sourceId === sourceId && e.targetId === targetId);
    if (exists) return;
    const id = `edge-${Date.now()}`;
    setEdges(prev => [...prev, { id, sourceId, targetId, protocol: 'HTTP', animated: simState === 'running' }]);
    setConnectingFrom(null);
  }, [edges, simState]);

  const handleDeleteEdge = useCallback((id: string) => {
    setEdges(prev => prev.filter(e => e.id !== id));
    if (selectedEdgeId === id) {
      setSelectedEdgeId(null);
      setPathMonitorOpen(false);
    }
  }, [selectedEdgeId]);

  // ── Keyboard: Delete/Backspace removes selected edge ─────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Only fire if no input/textarea is focused
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        if (selectedEdgeId) {
          handleDeleteEdge(selectedEdgeId);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEdgeId, handleDeleteEdge]);

  const handleLoadPreset = useCallback((preset: ArchitecturePreset) => {
    setSimState('stopped');
    setNodes(preset.nodes.map(n => ({ ...n, metrics: { rps: 0, latency: 0, errorRate: 0, cpuLoad: 0 } })));
    setEdges(preset.edges);
    setSelectedNodeId(null);
    setPresetsOpen(false);
  }, []);

  const handleClearCanvas = useCallback(() => {
    setSimState('stopped');
    setNodes([]);
    setEdges([]);
    setSelectedNodeId(null);
    setInspectorOpen(false);
  }, []);

  const handleUpdateNodeConfig = useCallback((id: string, config: Record<string, string | number | boolean>) => {
    setNodes(prev => prev.map(n => n.id === id ? { ...n, config: { ...n.config, ...config } } : n));
  }, []);

  const handleUpdateNodeLabel = useCallback((id: string, label: string) => {
    setNodes(prev => prev.map(n => n.id === id ? { ...n, label } : n));
  }, []);

  const handleTriggerExperiment = useCallback((nodeId: string, experiment: string) => {
    if (simState !== 'running') return;
    setChaosState(prev => {
      const next = { ...prev, activeEvents: new Set(prev.activeEvents), eventStartTick: new Map(prev.eventStartTick) };
      switch (experiment) {
        case 'Latency Spike': next.activeEvents.add('chaos-latency-spike');
          next.eventStartTick.set('chaos-latency-spike', tickRef.current);
          break;
        case 'Kill Node': next.activeEvents.add('chaos-kill-random');
          next.eventStartTick.set('chaos-kill-random', tickRef.current);
          next.killedNodeId = nodeId;
          break;
        case 'CPU Spike': next.activeEvents.add('chaos-cpu-throttle');
          next.eventStartTick.set('chaos-cpu-throttle', tickRef.current);
          break;
        case 'Network Partition': next.activeEvents.add('chaos-network-partition');
          next.eventStartTick.set('chaos-network-partition', tickRef.current);
          next.partitionGroupA = new Set([nodeId]);
          break;
        case 'Memory Pressure': next.activeEvents.add('chaos-memory-leak');
          next.eventStartTick.set('chaos-memory-leak', tickRef.current);
          break;
        case 'Gradual Degradation': next.activeEvents.add('chaos-cascade');
          next.eventStartTick.set('chaos-cascade', tickRef.current);
          next.killedNodeId = nodeId;
          break;
        default:
          break;
      }
      chaosRef.current = next as typeof prev;
      return next as typeof prev;
    });
  }, [simState]);

  return (
    <div className={`flex flex-col h-full overflow-hidden bg-background ${isLandscape ? 'game-scanlines relative' : ''}`}>
      {/* Landscape game HUD indicator */}
      {isLandscape && (
        <div className="absolute top-0 left-0 right-0 z-50 pointer-events-none flex items-center justify-between px-2" style={{ height: 0 }}>
          <div className="flex items-center gap-1 mt-1">
            <span className="text-[8px] font-bold tracking-widest uppercase text-primary/50 font-mono">ARCHSIM</span>
            <span className="w-1 h-1 rounded-full bg-primary/40 game-pulse" />
          </div>
          <div className="flex items-center gap-1 mt-1">
            <span className={`text-[8px] font-bold tracking-widest uppercase font-mono ${
              simState === 'running' ? 'text-positive text-game-glow' :
              simState === 'paused' ? 'text-warning' : 'text-muted-foreground/40'
            }`}>{simState.toUpperCase()}</span>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <CanvasToolbar
        simState={simState}
        setSimState={setSimState}
        paletteOpen={paletteOpen}
        setPaletteOpen={setPaletteOpen}
        onOpenPresets={() => setPresetsOpen(true)}
        onOpenSaveLoad={() => setSaveLoadOpen(true)}
        onOpenCompare={() => setCompareOpen(true)}
        onOpenBottleneck={() => setBottleneckOpen(!bottleneckOpen)}
        onOpenPathMonitor={() => setPathMonitorOpen(!pathMonitorOpen)}
        onClear={handleClearCanvas}
        nodeCount={nodes.length}
        edgeCount={edges.length}
        isLandscape={isLandscape}
      />

      {/* Main area */}
      <div
        className="flex flex-1 overflow-hidden relative"
        onDragOverCapture={event => {
          if (paletteOpen && window.innerWidth < 768) event.preventDefault();
        }}
        onDropCapture={event => {
          if (!paletteOpen || window.innerWidth >= 768) return;
          const defId = event.dataTransfer.getData('application/archsim-component');
          if (!defId) return;
          event.preventDefault();
          event.stopPropagation();
          window.dispatchEvent(new CustomEvent('archsim-component-drop', {
            detail: { defId, clientX: event.clientX, clientY: event.clientY },
          }));
        }}
      >
        {/* Palette — sidebar on desktop, slide-in overlay on mobile */}
        {paletteOpen && (
          <>
            {/* Palette panel — absolute on mobile (left drawer), relative on desktop (sidebar) */}
            <div className={`absolute md:relative top-0 left-0 h-full md:h-auto z-30 md:z-auto ${isLandscape ? 'landscape-palette' : ''}`}>
              <ComponentPalette
                onClose={() => setPaletteOpen(false)}
                simState={simState}
                isLandscape={isLandscape}
              />
            </div>
          </>
        )}

        {/* Canvas */}
        <div className="flex-1 relative overflow-hidden canvas-grid">
          <CanvasLayer
            nodes={nodes}
            edges={edges}
            selectedNodeId={selectedNodeId}
            selectedEdgeId={selectedEdgeId}
            simState={simState}
            connectingFrom={connectingFrom}
            onDropComponent={handleDropComponent}
            onMoveNode={handleMoveNode}
            onDeleteNode={handleDeleteNode}
            onSelectNode={(id) => {
              handleSelectNode(id);
            }}
            onConnectNodes={handleConnectNodes}
            onDeleteEdge={handleDeleteEdge}
            onSelectEdge={handleSelectEdge}
            onStartConnect={setConnectingFrom}
            onNodeClickForPopup={handleNodeClickForPopup}
          />

          {/* Node Monitor Popup */}
          {monitorPopup && (() => {
            const popupNode = nodes.find(n => n.id === monitorPopup.nodeId);
            if (!popupNode) return null;
            const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
            const popupWidth = isLandscape ? 260 : 320;
            const viewportW = typeof window !== 'undefined' ? window.innerWidth : 800;
            const viewportH = typeof window !== 'undefined' ? window.innerHeight : 600;
            const clampedX = Math.min(monitorPopup.x, viewportW - popupWidth - 16);
            const safeX = Math.max(16, clampedX);
            // In landscape: anchor to bottom-left; on mobile portrait: bottom; desktop: near click
            const popupStyle: React.CSSProperties = isLandscape
              ? { left: paletteOpen ? 68 : 16, bottom: 8, width: popupWidth }
              : isMobile
              ? { left: 12, right: 12, bottom: 'calc(12px + env(safe-area-inset-bottom))', width: 'auto' }
              : { left: safeX, top: Math.min(monitorPopup.y + 12, viewportH - 400) };
            return (
              <div
                className={`absolute z-40 pointer-events-auto ${isLandscape ? 'landscape-popup' : ''}`}
                style={popupStyle}
              >
                <NodeMonitorPopup
                  node={popupNode}
                  simState={simState}
                  simMetrics={simMetrics}
                  allNodes={nodes}
                  allEdges={edges}
                  onClose={() => setMonitorPopup(null)}
                  onOpenInspector={() => {
                    setSelectedNodeId(monitorPopup.nodeId);
                    setInspectorOpen(true);
                    setMonitorPopup(null);
                  }}
                  onUpdateConfig={handleUpdateNodeConfig}
                  isLandscape={isLandscape}
                />
              </div>
            );
          })()}

          {/* Bottleneck panel overlay */}
          {bottleneckOpen && (
            <div className={`absolute z-30 max-w-[calc(100vw-1.5rem)] ${isLandscape ? 'top-1 right-1' : 'top-3 right-3'}`}>
              <BottleneckPanel nodes={nodes} simState={simState} onClose={() => setBottleneckOpen(false)} />
            </div>
          )}

          {/* Empty state */}
          {nodes.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className={`text-center px-4 ${isLandscape ? 'hud-corner relative p-4' : ''}`}>
                <div className={`opacity-30 ${isLandscape ? 'text-3xl mb-2' : 'text-5xl mb-4'}`}>⬡</div>
                <p className={`text-muted-foreground font-medium ${isLandscape ? 'text-[10px]' : 'text-sm'}`}>
                  {isLandscape ? 'Drag components to start' : 'Drag components from the palette to start designing'}
                </p>
                {!isLandscape && <p className="text-muted-foreground/60 text-xs mt-1">Or load a preset architecture to explore</p>}
              </div>
            </div>
          )}
        </div>

        {/* Node Inspector — in landscape: right panel; mobile: bottom sheet; desktop: sidebar */}
        {inspectorOpen && selectedNode && (
          <>
            {/* Mobile/landscape backdrop */}
            <div
              className={`${isLandscape ? 'hidden' : 'md:hidden'} absolute inset-0 bg-background/60 z-20`}
              onClick={() => { setInspectorOpen(false); setSelectedNodeId(null); }}
            />
            <div className={`
              ${isLandscape
                ? 'absolute right-0 top-0 bottom-0 z-30 landscape-inspector overflow-y-auto' :'absolute bottom-0 left-0 right-0 md:relative md:bottom-auto md:left-auto md:right-auto z-30 md:z-auto max-h-[70vh] md:max-h-full overflow-y-auto md:overflow-visible'
              }
            `}>
              <NodeInspectorPanel
                node={selectedNode}
                simState={simState}
                onClose={() => { setInspectorOpen(false); setSelectedNodeId(null); }}
                onUpdateConfig={handleUpdateNodeConfig}
                onUpdateLabel={handleUpdateNodeLabel}
                onTriggerExperiment={handleTriggerExperiment}
                onDeleteNode={handleDeleteNode}
              />
            </div>
          </>
        )}
      </div>

      {/* Live Metrics Bar */}
      <LiveMetricsBar metrics={simMetrics} simState={simState} isLandscape={isLandscape} />

      {/* Path Monitor Drawer */}
      {pathMonitorOpen && (
        <PathMonitorDrawer
          nodes={nodes}
          edges={edges}
          selectedEdgeId={selectedEdgeId}
          simState={simState}
          metrics={simMetrics}
          chaosState={chaosState}
          onToggleChaos={handleToggleChaos}
          onDeleteEdge={handleDeleteEdge}
          onClose={() => { setPathMonitorOpen(false); setSelectedEdgeId(null); }}
        />
      )}

      {/* Modals */}
      {presetsOpen && (
        <PresetsPanel onLoad={handleLoadPreset} onClose={() => setPresetsOpen(false)} />
      )}
      {saveLoadOpen && (
        <SaveLoadModal
          nodes={nodes}
          edges={edges}
          onLoad={(arch) => {
            setNodes(arch.nodes);
            setEdges(arch.edges);
            setSaveLoadOpen(false);
          }}
          onClose={() => setSaveLoadOpen(false)}
        />
      )}
      {compareOpen && (
        <ComparePanel onClose={() => setCompareOpen(false)} />
      )}
    </div>
  );
}

// ─── Arrival rate estimation ──────────────────────────────────────────────────

/**
 * Compute the total RPS injected by client nodes that have at least one
 * outgoing connection. Each client contributes its own config.clientRps value.
 */
function computeConnectedClientRps(
  allNodes: CanvasNode[],
  edges: CanvasEdge[]
): number {
  const clientNodes = allNodes.filter(n => {
    const def = ALL_COMPONENTS.find(c => c.id === n.defId);
    return def?.group === 'Clients';
  });

  if (clientNodes.length === 0) {
    // No client nodes on canvas — check if any non-client source nodes exist
    // with outgoing edges (e.g. a standalone server acting as entry point)
    const hasIncoming = new Set(edges.map(e => e.targetId));
    const sourceNodes = allNodes.filter(n => !hasIncoming.has(n.id));
    const connectedSources = sourceNodes.filter(n =>
      edges.some(e => e.sourceId === n.id)
    );
    // Use a default fallback RPS of 1000 for non-client source nodes
    return connectedSources.length > 0 ? 1000 : 0;
  }

  // Sum RPS of each connected client node (those with at least one outgoing edge)
  return clientNodes.reduce((sum, n) => {
    const hasOutgoing = edges.some(e => e.sourceId === n.id);
    if (!hasOutgoing) return sum;
    return sum + ((n.config.clientRps as number) ?? 1000);
  }, 0);
}

// ─── Hops estimation ────────────────────────────────────────────────────────

/**
 * Estimate the number of hops from a given node to the source (root).
 * This is a best-effort estimate based on the current topology.
 */
function estimateHopsToSource(
  node: CanvasNode,
  allNodes: CanvasNode[],
  edges: CanvasEdge[]
): number {
  const visited = new Set<string>();
  const queue: { node: CanvasNode; hops: number }[] = [{ node, hops: 0 }];

  while (queue.length > 0) {
    const { node: current, hops } = queue.shift()!;
    if (visited.has(current.id)) continue;
    visited.add(current.id);

    // If the current node is a source, return the number of hops
    if (isSourceNode(current, allNodes, edges)) return hops;

    // Add unvisited predecessors to the queue
    const predecessors = edges
      .filter(e => e.targetId === current.id)
      .map(e => e.sourceId)
      .filter(id => !visited.has(id))
      .map(id => allNodes.find(n => n.id === id)!);
    for (const pred of predecessors) {
      queue.push({ node: pred, hops: hops + 1 });
    }
  }

  // If no path to the source is found, return a large number to indicate failure
  return Infinity;
}

// ─── Source node detection ────────────────────────────────────────────────────

/**
 * Determine whether a given node is a source (root) in the topology.
 * A source node has no incoming edges or is directly connected to the source.
 */
function isSourceNode(
  node: CanvasNode,
  allNodes: CanvasNode[],
  edges: CanvasEdge[]
): boolean {
  // Check for incoming edges
  const hasIncoming = edges.some(e => e.targetId === node.id);
  if (!hasIncoming) return true; // No incoming edges, so it's a source

  // Check for direct connection to the source
  const sourceId = 'source'; // Assuming 'source' is the ID of the source node
  const isDirectlyConnected = edges.some(e => e.sourceId === sourceId && e.targetId === node.id);
  if (isDirectlyConnected) return true; // Directly connected to source

  return false;
}
